import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Image,
  Download,
  Trash2,
  Loader2,
  Plus,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/dashboard/DashboardLayout";

interface Thumbnail {
  id: string;
  image_url: string;
  title: string;
  style: string;
  emotion: string;
  created_at: string;
}

const MyThumbnails = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchThumbnails();
    }
  }, [user]);

  const fetchThumbnails = async () => {
    try {
      const { data, error } = await supabase
        .from('thumbnails')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching thumbnails:', error);
        toast.error('Failed to load thumbnails');
        return;
      }

      // Generate signed URLs for private bucket thumbnails
      const thumbnailsWithSignedUrls = await Promise.all(
        (data || []).map(async (thumb) => {
          if (thumb.image_url.includes('/storage/v1/')) {
            const pathMatch = thumb.image_url.match(/thumbnails\/(.+?)(?:\?|$)/);
            if (pathMatch) {
              const filePath = decodeURIComponent(pathMatch[1]);
              const { data: signedUrlData } = await supabase.storage
                .from('thumbnails')
                .createSignedUrl(filePath, 60 * 60); // 1 hour
              if (signedUrlData?.signedUrl) {
                return { ...thumb, image_url: signedUrlData.signedUrl };
              }
            }
          }
          return thumb;
        })
      );
      setThumbnails(thumbnailsWithSignedUrls);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load thumbnails');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (thumbnail: Thumbnail) => {
    try {
      const response = await fetch(thumbnail.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${thumbnail.title.replace(/[^a-z0-9]/gi, '-')}-${thumbnail.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Thumbnail downloaded!");
    } catch (err) {
      console.error('Download error:', err);
      toast.error("Failed to download thumbnail");
    }
  };

  const handleDelete = async (thumbnail: Thumbnail) => {
    try {
      // Delete from storage if it's a storage URL
      if (thumbnail.image_url.includes('thumbnails') && user) {
        const pathMatch = thumbnail.image_url.match(/thumbnails\/(.+)$/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          await supabase.storage.from('thumbnails').remove([filePath]);
        }
      }

      // Delete from database
      const { error } = await supabase
        .from('thumbnails')
        .delete()
        .eq('id', thumbnail.id);

      if (error) {
        console.error('Error deleting thumbnail:', error);
        toast.error('Failed to delete thumbnail');
        return;
      }

      setThumbnails(prev => prev.filter(t => t.id !== thumbnail.id));
      toast.success("Thumbnail deleted");
    } catch (err) {
      console.error('Delete error:', err);
      toast.error("Failed to delete thumbnail");
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <DashboardLayout activeTab="thumbnails">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">My Thumbnails</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {thumbnails.length} thumbnail{thumbnails.length !== 1 ? 's' : ''} saved
              </p>
            </div>
            <Link to="/dashboard">
              <Button variant="gradient" size="sm">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Create New</span>
              </Button>
            </Link>
          </div>
        </header>

        <div className="p-4 sm:p-6">
          {thumbnails.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
              {thumbnails.map((thumbnail) => (
                <div
                  key={thumbnail.id}
                  className="group relative rounded-xl border border-border overflow-hidden bg-card hover:border-primary/50 transition-all"
                >
                  <div className="aspect-video bg-secondary">
                    <img
                      src={thumbnail.image_url}
                      alt={thumbnail.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium truncate">{thumbnail.title}</h3>
                    <p className="text-xs text-muted-foreground">
                      {new Date(thumbnail.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleDownload(thumbnail)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(thumbnail)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-xl gradient-bg flex items-center justify-center mb-4">
                <Image className="w-8 h-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No thumbnails yet</h3>
              <p className="text-muted-foreground mb-6">
                Generate your first thumbnail to get started
              </p>
              <Link to="/dashboard">
                <Button variant="gradient">
                  <Sparkles className="w-4 h-4" />
                  Create Thumbnail
                </Button>
              </Link>
            </div>
          )}
        </div>
    </DashboardLayout>
  );
};

export default MyThumbnails;
